import { useState, useEffect, useMemo } from "react";
import { surveysService } from "@/api/surveys.service";
import {
  FALLBACK_TRANSLATION_LANGUAGES,
  LANGUAGE_LABEL_MAP,
  TRANSLATION_SOURCE_LANGUAGE,
} from "@/pages/Surveys/Details/surveyDetails.constants";

export type LanguageOption = { value: string; label: string };

type UseSupportedLanguagesResult = {
  allLanguages: LanguageOption[];
  translatableLanguages: string[];
  loading: boolean;
};

const FALLBACK_ALL = [TRANSLATION_SOURCE_LANGUAGE, ...FALLBACK_TRANSLATION_LANGUAGES];

function toOptions(codes: string[]): LanguageOption[] {
  return codes.map(code => ({
    value: code,
    label: LANGUAGE_LABEL_MAP[code] ?? code.toUpperCase(),
  }));
}

export function useSupportedLanguages(): UseSupportedLanguagesResult {
  const [languages, setLanguages] = useState<string[]>(FALLBACK_ALL);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    surveysService
      .getSupportedLanguages()
      .then(({ languages: langs }) => {
        if (langs.length > 0) setLanguages(langs);
      })
      .catch(() => {
        // keep fallback
      })
      .finally(() => setLoading(false));
  }, []);

  const allLanguages = useMemo(() => toOptions(languages), [languages]);
  const translatableLanguages = useMemo(
    () => languages.filter(l => l !== TRANSLATION_SOURCE_LANGUAGE),
    [languages],
  );

  return {
    allLanguages,
    translatableLanguages,
    loading,
  };
}
